import { UserRole } from '@alert-manager/lib';
import { Bell, BookOpen, ChevronLeft, ChevronRight, ExternalLink, LogOut, Moon, ScrollText, Settings, Shield, Sun, User } from 'lucide-react';
import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useVersion } from '../hooks/useVersion';

export type NavPage = 'dashboard' | 'logs' | 'settings';

interface SidebarProps {
  activePage: NavPage;
  onNavigate: (page: NavPage) => void;
}

/* ── Pill-slider toggle (matches lens-ops) ─────────────────────── */
function PillToggle({
  left,
  right,
  isRight,
  onToggle,
  title,
  dark = true,
}: {
  left: string;
  right: string;
  isRight: boolean;
  onToggle: () => void;
  title?: string;
  dark?: boolean;
}) {
  return (
    <button
      onClick={onToggle}
      title={title}
      className={`relative flex items-center h-7 rounded-full p-0.5 cursor-pointer select-none w-full ${dark ? 'bg-slate-700/60' : 'bg-slate-200'}`}
    >
      <span
        className="absolute h-6 rounded-full transition-all duration-200 ease-in-out bg-blue-600"
        style={{ width: '50%', left: isRight ? '50%' : '0%' }}
      />
      <span
        className={`relative z-10 flex-1 text-center text-[10px] font-bold uppercase tracking-wider transition-colors duration-200 ${
          !isRight ? 'text-white' : dark ? 'text-slate-400' : 'text-slate-500'
        }`}
      >
        {left}
      </span>
      <span
        className={`relative z-10 flex-1 text-center text-[10px] font-bold uppercase tracking-wider transition-colors duration-200 ${
          isRight ? 'text-white' : dark ? 'text-slate-400' : 'text-slate-500'
        }`}
      >
        {right}
      </span>
    </button>
  );
}

/* ── Navigation items ──────────────────────────────────────────── */
const NAV_ITEMS: { page: NavPage; label: string; icon: typeof Bell; adminOnly?: boolean }[] = [
  { page: 'dashboard', label: 'Dashboard', icon: Bell },
  { page: 'logs', label: 'Logs', icon: ScrollText },
  { page: 'settings', label: 'Settings', icon: Settings },
];

const Sidebar: React.FC<SidebarProps> = ({ activePage, onNavigate }) => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const version = useVersion();
  const isDark = theme === 'dark';
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('am-sidebar-collapsed') === '1');

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem('am-sidebar-collapsed', next ? '1' : '0');
  };

  const initial = user?.username?.charAt(0)?.toUpperCase() || '?';

  return (
    <div className="relative shrink-0 print:hidden transition-all duration-300 ease-in-out" style={{ width: collapsed ? 64 : 256 }}>
      {/* Edge collapse handle */}
      <button
        onClick={toggleCollapsed}
        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        className={`absolute z-20 top-1/2 -translate-y-1/2 -right-3 w-6 h-10 rounded-r-md flex items-center justify-center
          transition-colors duration-150 cursor-pointer shadow-sm
          ${
            isDark
              ? 'bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-slate-200 border border-l-0 border-slate-700'
              : 'bg-white hover:bg-slate-50 text-slate-400 hover:text-slate-600 border border-l-0 border-slate-200'
          }`}
      >
        {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
      </button>

      <aside
        className="flex flex-col h-screen sticky top-0 transition-all duration-300 ease-in-out border-r overflow-hidden"
        style={{ backgroundColor: 'var(--sidebar-bg)', borderColor: 'var(--sidebar-border)' }}
      >
        {/* Brand */}
        <div className="shrink-0 border-b" style={{ borderColor: 'var(--sidebar-border)' }}>
          <div className={`flex items-center gap-2.5 px-3 ${collapsed ? 'justify-center h-14' : 'h-12'}`}>
            <Bell size={20} className="shrink-0 text-blue-500" />
            {!collapsed && (
              <span className={`font-extrabold text-base tracking-tight shrink-0 ${isDark ? 'text-white' : 'text-slate-900'}`}>Alert Manager</span>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-2 px-2 space-y-0.5">
          {NAV_ITEMS.map(({ page, label, icon: Icon }) => {
            const isActive = activePage === page;
            return (
              <button
                key={page}
                onClick={() => onNavigate(page)}
                title={collapsed ? label : undefined}
                className={`w-full flex items-center gap-3 rounded-lg transition-colors duration-150 cursor-pointer ${
                  collapsed ? 'justify-center px-2 py-2.5' : 'pl-4 pr-3 py-2'
                } ${
                  isActive
                    ? isDark
                      ? 'bg-blue-600/20 text-blue-400'
                      : 'bg-blue-50 text-blue-600'
                    : isDark
                      ? 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'
                      : 'text-slate-500 hover:bg-slate-100 hover:text-slate-800'
                }`}
              >
                <Icon size={16} className="shrink-0" />
                {!collapsed && <span className="text-[13px] font-medium truncate">{label}</span>}
              </button>
            );
          })}

          {/* API Docs link (admin only) */}
          {user?.role === UserRole.ADMIN && (
            <a
              href="/docs"
              target="_blank"
              rel="noopener noreferrer"
              title={collapsed ? 'API Docs' : undefined}
              className={`w-full flex items-center gap-3 rounded-lg transition-colors duration-150 ${
                collapsed ? 'justify-center px-2 py-2.5' : 'pl-4 pr-3 py-2'
              } ${isDark ? 'text-slate-400 hover:bg-slate-800 hover:text-slate-200' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-800'}`}
            >
              <BookOpen size={16} className="shrink-0" />
              {!collapsed && (
                <>
                  <span className="text-[13px] font-medium truncate">API Docs</span>
                  <ExternalLink size={10} className="ml-auto opacity-40 shrink-0" />
                </>
              )}
            </a>
          )}
        </nav>

        {/* Bottom — user info + theme toggle */}
        <div className="border-t px-2 py-3 space-y-3" style={{ borderColor: 'var(--sidebar-border)' }}>
          {/* User button */}
          {user && !collapsed && (
            <div className="px-1">
              <div className="flex items-center gap-2.5 px-1 py-1">
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-white text-[10px] font-bold ${
                    user.role === UserRole.ADMIN ? 'bg-gradient-to-br from-amber-500 to-orange-600' : 'bg-gradient-to-br from-blue-500 to-indigo-600'
                  }`}
                >
                  {initial}
                </div>
                <div className="min-w-0 flex-1 text-left">
                  <div className="flex items-center gap-1.5">
                    <span className={`text-[12px] font-medium truncate ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{user.username}</span>
                    <span
                      className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide shrink-0 ${
                        user.role === UserRole.ADMIN
                          ? isDark
                            ? 'bg-amber-500/15 text-amber-400'
                            : 'bg-amber-100 text-amber-700'
                          : isDark
                            ? 'bg-slate-700 text-slate-400'
                            : 'bg-slate-200 text-slate-500'
                      }`}
                    >
                      {user.role === UserRole.ADMIN ? <Shield size={8} /> : <User size={8} />}
                      {user.role}
                    </span>
                  </div>
                </div>
              </div>
              {/* Logout */}
              <button
                onClick={logout}
                className={`w-full flex items-center justify-center gap-1.5 px-2 py-1.5 mt-1 rounded-lg text-[11px] font-medium transition-colors cursor-pointer ${
                  isDark ? 'text-red-400 hover:bg-red-500/10' : 'text-red-600 hover:bg-red-50'
                }`}
              >
                <LogOut size={11} />
                Sign Out
              </button>
            </div>
          )}
          {user && collapsed && (
            <div className="flex flex-col items-center gap-1">
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-[10px] font-bold ${
                  user.role === UserRole.ADMIN ? 'bg-gradient-to-br from-amber-500 to-orange-600' : 'bg-gradient-to-br from-blue-500 to-indigo-600'
                }`}
                title={user.username}
              >
                {initial}
              </div>
              <button
                onClick={logout}
                title="Sign Out"
                className={`w-full flex items-center justify-center py-2 rounded-lg transition-colors cursor-pointer ${
                  isDark ? 'text-red-400 hover:bg-red-500/10' : 'text-red-600 hover:bg-red-50'
                }`}
              >
                <LogOut size={14} />
              </button>
            </div>
          )}

          {/* Theme toggle */}
          {!collapsed ? (
            <div className="px-1">
              <PillToggle
                left="Light"
                right="Dark"
                isRight={isDark}
                onToggle={toggleTheme}
                title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                dark={isDark}
              />
            </div>
          ) : (
            <button
              onClick={toggleTheme}
              title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              className={`w-full flex items-center justify-center py-2 rounded-lg transition-colors cursor-pointer ${
                isDark ? 'text-slate-400 hover:bg-slate-800 hover:text-slate-200' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-800'
              }`}
            >
              {isDark ? <Sun size={16} /> : <Moon size={16} />}
            </button>
          )}

          {/* Version */}
          <div className={`text-center text-[10px] font-mono ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>
            {collapsed ? `v${version.split('.').slice(-1)}` : `v${version}`}
          </div>
        </div>
      </aside>
    </div>
  );
};

export default Sidebar;
